import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribeToNewsletter } from '../services/api';
import { formatFormUrl } from '../utils/urlHelpers';

function Footer() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  
  // Clear status message after 5 seconds
  useEffect(() => {
    if (subscribeStatus === 'success' || subscribeStatus === 'error') {
      const timer = setTimeout(() => {
        setSubscribeStatus(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [subscribeStatus]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubscribeStatus('loading');
      await subscribeToNewsletter({ 
        email,
        source: 'website_footer' 
      });
      setSubscribeStatus('success');
      setEmail('');
    } catch (error) {
      setSubscribeStatus('error');
      console.error('Error subscribing to newsletter:', error);
    }
  };

  return (
    <footer className="footer-component">
      <div className="container">
        {/* Newsletter Section */}
        <div className="footer-top">
          <div className="newsletter-section">
            <h2 className="heading-style-h1">Join our newsletter</h2>
            <form className="newsletter-form" onSubmit={handleSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email" 
                className="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button 
                type="submit" 
                className="btn-subscribe"
                disabled={subscribeStatus === 'loading'}
              >
                {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
              </button>
              
              <div className="status-message-container">
                {subscribeStatus === 'success' && (
                  <div className="success-message">
                    <span className="status-icon" role="img" aria-label="success">✓</span> 
                    Successfully subscribed!
                  </div>
                )}
                {subscribeStatus === 'error' && (
                  <div className="error-message">
                    <span className="status-icon" role="img" aria-label="error">✗</span> 
                    Failed to subscribe. Please try again.
                  </div>
                )}
              </div>
              
              <div className="recaptcha-placeholder">
                {/* reCAPTCHA would be integrated here */}
                {/* <div className="recaptcha-box"></div> */}
              </div>
            </form>
          </div>
        </div>
        
        {/* Logo and Social Links */}
        <div className="footer-middle-section">
          <div className="logo-section">
            <div className="footer-logo">
              <svg width="120" height="44" viewBox="0 0 282 104" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="paint0_linear_241_27" x1="0" y1="0" x2="282.286" y2="104" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#8F05FC"></stop>
                    <stop offset="1" stopColor="#5024FF"></stop>
                  </linearGradient>
                </defs>
                <text x="10" y="70" fontSize="90" fontWeight="bold" fill="url(#paint0_linear_241_27)" fontFamily="Arial, sans-serif">rkube</text>
              </svg>
            </div>
            <div className="tagline">| US tax and accounting automation</div>
            <div className="trustpilot">
              <div className="trustpilot-widget">
                Review us on <img src="/images/trustpilot.png" alt="Trustpilot" className="trustpilot-img" />
              </div>
            </div>
          </div>
          
          <div className="social-links-container">
            <a href="https://x.com/rkubehq" target="_blank" rel="noopener noreferrer" className="social-link-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.1761 4H19.9362L13.9061 10.7774L21 20H15.4456L11.0951 14.4066L6.11723 20H3.35544L9.80517 12.7508L3 4H8.69545L12.6279 9.11262L17.1761 4ZM16.2073 18.3754H17.7368L7.86441 5.53928H6.2232L16.2073 18.3754Z" fill="currentColor"></path>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/rkube/" target="_blank" rel="noopener noreferrer" className="social-link-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M4.5 3C3.67157 3 3 3.67157 3 4.5V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V4.5C21 3.67157 20.3284 3 19.5 3H4.5ZM8.52076 7.00272C8.52639 7.95897 7.81061 8.54819 6.96123 8.54397C6.16107 8.53975 5.46357 7.90272 5.46779 7.00413C5.47201 6.15897 6.13998 5.47975 7.00764 5.49944C7.88795 5.51913 8.52639 6.1646 8.52076 7.00272ZM12.2797 9.76176H9.75971H9.7583V18.3216H12.4217V18.1219C12.4217 17.742 12.4214 17.362 12.4211 16.9819V16.9818V16.9816V16.9815V16.9812C12.4203 15.9674 12.4194 14.9532 12.4246 13.9397C12.426 13.6936 12.4372 13.4377 12.5005 13.2028C12.7381 12.3253 13.5271 11.7586 14.4074 11.8979C14.9727 11.9864 15.3467 12.3141 15.5042 12.8471C15.6013 13.1803 15.6449 13.5389 15.6491 13.8863C15.6605 14.9339 15.6589 15.9815 15.6573 17.0292V17.0294C15.6567 17.3992 15.6561 17.769 15.6561 18.1388V18.3202H18.328V18.1149C18.328 17.6629 18.3278 17.211 18.3275 16.7591V16.759V16.7588C18.327 15.6293 18.3264 14.5001 18.3294 13.3702C18.3308 12.8597 18.276 12.3563 18.1508 11.8627C17.9638 11.1286 17.5771 10.5211 16.9485 10.0824C16.5027 9.77019 16.0133 9.5691 15.4663 9.5466C15.404 9.54401 15.3412 9.54062 15.2781 9.53721L15.2781 9.53721L15.2781 9.53721C14.9984 9.52209 14.7141 9.50673 14.4467 9.56066C13.6817 9.71394 13.0096 10.0641 12.5019 10.6814C12.4429 10.7522 12.3852 10.8241 12.2991 10.9314L12.2991 10.9315L12.2797 10.9557V9.76176ZM5.68164 18.3244H8.33242V9.76733H5.68164V18.3244Z" fill="currentColor"></path>
              </svg>
            </a>
            <a href="https://www.youtube.com/@rkube" target="_blank" rel="noopener noreferrer" className="social-link-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M20.5686 4.77345C21.5163 5.02692 22.2555 5.76903 22.5118 6.71673C23.1821 9.42042 23.1385 14.5321 22.5259 17.278C22.2724 18.2257 21.5303 18.965 20.5826 19.2213C17.9071 19.8831 5.92356 19.8015 3.40294 19.2213C2.45524 18.9678 1.71595 18.2257 1.45966 17.278C0.827391 14.7011 0.871044 9.25144 1.44558 6.73081C1.69905 5.78311 2.44116 5.04382 3.38886 4.78753C6.96561 4.0412 19.2956 4.282 20.5686 4.77345ZM9.86682 8.70227L15.6122 11.9974L9.86682 15.2925V8.70227Z" fill="currentColor"></path>
              </svg>
            </a>
          </div>
        </div>
        
        {/* Footer Links */}
        <div className="footer-links-grid">
                    
          {/* Filings */}
          <div className="footer-column-full">
            <h4 className="footer-heading">Filings</h4>
            <div className="filings-grid">
              <div className="filings-column">
                <Link to={formatFormUrl("Assumed Business Name Certificate")} className="footer-link-item">Assumed Business Name Certificate</Link>
                <Link to={formatFormUrl("Certified Copies")} className="footer-link-item">Certified Copies</Link>
                <Link to={formatFormUrl("Certificate of Good Standing")} className="footer-link-item">Certificate of Good Standing</Link>
                <Link to={formatFormUrl("Articles of Dissolution (L-07, B-05, B-06)")} className="footer-link-item">Articles of Dissolution</Link>
                <Link to={formatFormUrl("Annual Report")} className="footer-link-item">Annual Report</Link>
                <Link to={formatFormUrl("Trade Name Certificate")} className="footer-link-item">Trade Name Certificate</Link>
                <Link to={formatFormUrl("Amendment of Articles (L-17)")} className="footer-link-item">Amendment of Articles</Link>
                <Link to={formatFormUrl("Change of Registered Agent (BE-06)")} className="footer-link-item">Change of Registered Agent</Link>
                <Link to={formatFormUrl("Application for Reinstatement (L-08)")} className="footer-link-item">Application for Reinstatement</Link>
              </div>
              <div className="filings-column">
                <Link to={formatFormUrl("Articles of Organization (L-01)")} className="footer-link-item">Articles of Organization</Link>
                <Link to={formatFormUrl("Resignation of Agent (BE-07)")} className="footer-link-item">Resignation of Agent</Link>
                <Link to={formatFormUrl("IRS Form 7004")} className="footer-link-item">IRS Form 7004</Link>
                <Link to={formatFormUrl("IRS Form 5471")} className="footer-link-item">IRS Form 5471</Link>
                <Link to={formatFormUrl("IRS Form 5472")} className="footer-link-item">IRS Form 5472</Link>
                <Link to={formatFormUrl("IRS Form 1120")} className="footer-link-item">IRS Form 1120</Link>
                <Link to={formatFormUrl("IRS Form 1099 Series")} className="footer-link-item">IRS Form 1099 Series</Link>
                <Link to={formatFormUrl("FinCEN Form 114 (FBAR)")} className="footer-link-item">FinCEN Form 114</Link>
                <Link to={formatFormUrl("409A Valuation")} className="footer-link-item">409A Valuation</Link>
                <Link to={formatFormUrl("FinCEN BOI Report")} className="footer-link-item">FinCEN BOI Report</Link>
              </div>
            </div>
          </div>

          <div className="footer-bottom-columns">
            {/* Services */}
            <div className="footer-column">
              <h4 className="footer-heading">Our Services</h4>
              <a href="/books" className="footer-link-item">Books</a>
              <a href="/tax" className="footer-link-item">Tax</a>
              <a href="/compliance" className="footer-link-item">Compliance</a>
              <a href="/company" className="footer-link-item">Company</a>
              <a href="/people" className="footer-link-item">People</a>
              <a href="/equity" className="footer-link-item">Equity</a>
            </div>
            
            {/* Free Tools */}
            <div className="footer-column">
              <h4 className="footer-heading">Free Tools</h4>
              <a href="/free-tools/deadline-alerts" className="footer-link-item">Deadline alerts</a>
              {/* <a href="/free-tools/dft-calculator" className="footer-link-item">DFT calculator</a> */}
              {/* <a href="/free-tools/section-174-calculator" className="footer-link-item">Section 174 calculator</a> */}
              <a href="/free-tools/currency-converter" className="footer-link-item">Currency Converter</a>
            </div>
            
            {/* Resources - Moved to Community's position */}
            <div className="footer-column">
              <h4 className="footer-heading">Resources</h4>
              <a href="/blog" className="footer-link-item">Blog</a>
              <a href="/product-sheets" className="footer-link-item">Products we support</a>
              {/* <a href="/case-studies" className="footer-link-item">Case study</a> */}
              {/* <a href="/events" className="footer-link-item">Events</a> */}
              <a href="/perks" className="footer-link-item">Perks</a>
            </div>
            {/* Company */}
            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <a href="/contact" className="footer-link-item">Contact us</a>
              <a href="/about" className="footer-link-item">About us</a>
              {/* <a href="/pricing" className="footer-link-item">Pricing</a> */}
              <a href="/careers" className="footer-link-item">Careers</a>
              {/* <a href="/security" className="footer-link-item">Security</a> */}
              <a href="/privacy-policy" className="footer-link-item">Privacy policy</a>
              <a href="/terms-of-service" className="footer-link-item">Terms of service</a>
            </div>
          </div>

        </div>
        
        {/* Copyright */}
        <div className="footer-copyright">
          <p>Copyright ©️ rkube {new Date().getFullYear()}</p>
          <small style={{ color: 'white', opacity: 0.5, fontSize: '10px', marginTop: '5px', display: 'block' }}>
            Last updated: {new Date().toLocaleString()}
          </small>
        </div>
      </div>
    </footer>
  );
}

export default Footer;